import { type FormEvent, useState } from 'react';

import { Button } from '@/modules/ui/components/button/Button';
import { InlineError } from '@/modules/ui/components/inline-error/InlineError';
import { Input } from '@/modules/ui/components/input/Input';
import { useUpdateMeetingUrlMutation } from '@/modules/users/mutations/useUpdateMeetingUrlMutation';
import { actionErrorMessage } from '@/utils/actionErrorMessage';

const isBookingUrl = (value: string) => {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
};

interface BookingLinkFormProps {
  /** The saved link to edit; empty for a first-time setup. */
  initialValue?: string | null;
  submitLabel: string;
  /** Runs after the link is saved on the signed-in user. */
  onSaved?: (meetingUrl: string) => void;
  onCancel?: () => void;
}

/**
 * The signed-in user's booking link (Calendly or any scheduling page). It can
 * be replaced but never cleared, because hiring manager pre-screening sends it
 * to candidates; an empty value is rejected here and by the API.
 */
export function BookingLinkForm({
  initialValue,
  submitLabel,
  onSaved,
  onCancel,
}: BookingLinkFormProps) {
  const [value, setValue] = useState(initialValue ?? '');
  const [error, setError] = useState<string | null>(null);
  const updateMeetingUrl = useUpdateMeetingUrlMutation();

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const meetingUrl = value.trim();
    if (!isBookingUrl(meetingUrl)) {
      setError('Enter a booking link starting with https://.');
      return;
    }
    setError(null);
    updateMeetingUrl.mutate(meetingUrl, {
      onSuccess: () => onSaved?.(meetingUrl),
      onError: (mutationError) =>
        setError(actionErrorMessage('save your booking link', mutationError)),
    });
  };

  return (
    <form className="flex flex-col gap-1.5" noValidate onSubmit={submit}>
      <label className="text-[13px] font-medium" htmlFor="booking-link">
        Your booking link
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          aria-describedby={error ? 'booking-link-error' : undefined}
          aria-invalid={error !== null}
          className="max-w-[360px] min-w-[220px] flex-1"
          id="booking-link"
          inputMode="url"
          onChange={(event) => setValue(event.target.value)}
          placeholder="https://calendly.com/your-name/intro"
          type="url"
          value={value}
        />
        <Button
          className="h-8 rounded-md"
          loading={updateMeetingUrl.isPending}
          size="sm"
          type="submit"
        >
          {submitLabel}
        </Button>
        {onCancel && (
          <Button
            className="h-8 rounded-md"
            disabled={updateMeetingUrl.isPending}
            onClick={onCancel}
            size="sm"
            type="button"
            variant="ghost"
          >
            Cancel
          </Button>
        )}
      </div>
      {error && <InlineError id="booking-link-error">{error}</InlineError>}
    </form>
  );
}
